import { AlertCircle } from 'lucide-react'

import { useReport } from '@/hooks/features/reports'
import { useBusiness } from '@/contexts/BusinessContext'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'

import { PageHeader } from '@/components/atoms'
import { StatCard } from '@/components/dashboard/StatCard'
import { ReportToolbar } from '@/components/reports/ReportToolbar'
import { useReportRange } from '@/components/reports/useReportRange'

import type { CustomerReport as CustomerReportData } from '@/types/reports'

/** Customers: sign-ups, buyers (first-time and returning) and the top buyers by net spend in the range. */
const CustomerReport = () => {
  const { formatCurrency } = useBusiness()
  const { preset, range, onPresetChange, onRangeChange } = useReportRange()
  const report = useReport<CustomerReportData>('customers', { ...range, limit: 20 })
  const r = report.data
  const share = (n: number) => (r?.buyers ? `${Math.round((n / r.buyers) * 100)}% of buyers` : 'No buyers yet')

  return (
    <div className="space-y-4">
      <PageHeader title="Customer report" description="Who signed up, who bought, and who came back. Guest orders count as sales but not as customers." />
      <ReportToolbar preset={preset} range={range} onPresetChange={onPresetChange} onRangeChange={onRangeChange} downloads={[{ name: 'customers', label: 'New customers (Excel)' }]} />

      {report.isError && (
        <Alert variant="destructive">
          <AlertCircle className="size-4" />
          <AlertTitle>Could not load the report</AlertTitle>
          <AlertDescription>{report.error.message}</AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {r ? (
          <>
            <StatCard title="New sign-ups" value={r.new_customers.toLocaleString()} change={0} trend="neutral" note="Accounts created" />
            <StatCard title="Buying customers" value={r.buyers.toLocaleString()} change={0} trend="neutral" note="With at least one sale" />
            <StatCard title="First-time buyers" value={r.first_time_buyers.toLocaleString()} change={0} trend="neutral" note={share(r.first_time_buyers)} />
            <StatCard title="Returning buyers" value={r.returning_buyers.toLocaleString()} change={0} trend="neutral" note={share(r.returning_buyers)} />
          </>
        ) : (
          Array.from({ length: 4 }, (_, i) => <Skeleton key={i} className="h-24" />)
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="w-fit text-sm font-semibold underline decoration-faint decoration-dotted underline-offset-4">Top customers</CardTitle>
        </CardHeader>
        <CardContent>
          {!r ? (
            <Skeleton className="h-48" />
          ) : !r.top_customers.length ? (
            <p className="py-6 text-center text-sm text-muted-foreground">No customer bought in these dates.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Customer</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead className="text-right">Orders</TableHead>
                  <TableHead className="text-right">Spent</TableHead>
                  <TableHead>Last order</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {r.top_customers.map((c) => (
                  <TableRow key={c.customer_id}>
                    <TableCell className="font-medium">
                      {c.name} {c.returning ? <Badge tone="info" className="ml-1">Returning</Badge> : <Badge tone="success" className="ml-1">New buyer</Badge>}
                    </TableCell>
                    <TableCell className="text-muted-foreground">{c.phone || '—'}</TableCell>
                    <TableCell className="text-right tabular-nums">{c.orders}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatCurrency(c.spent)}</TableCell>
                    <TableCell className="text-muted-foreground">{new Date(c.last_order_at).toLocaleDateString(undefined, { dateStyle: 'medium' })}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export default CustomerReport
